import { useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { sendRequestConfirmation } from '../services/emailService';
import { z, ZodTypeAny } from 'zod';

const quoteSchema = z.object({
  name: z.string().min(2, { message: 'Name too short' }),
  email: z.string().email({ message: 'Invalid email' }),
  phone: z
    .string()
    .regex(/^\d{10,15}$/, { message: 'Invalid phone number' }),
  pickupLocation: z
    .string()
    .min(5, { message: 'Please enter a complete address' }),
  deliveryLocation: z
    .string()
    .min(5, { message: 'Please enter a complete address' }),
  vehicleType: z.string().min(1, { message: 'Vehicle type is required' }),
});

export type QuoteFormData = z.infer<typeof quoteSchema>;

type QuoteAddressCoordinates = {
  pickup?: [number, number];
  delivery?: [number, number];
};

export const useQuote = () => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const validateField = async (name: string, value: string) => {
    try {
      const fieldSchema = quoteSchema.shape[
        name as keyof typeof quoteSchema.shape
      ] as ZodTypeAny;
      const result = fieldSchema.safeParse(value);
      if (result.success) {
        setErrors(prev => ({ ...prev, [name]: '' }));
        return true;
      }
      const issue = result.error.issues[0];
      setErrors(prev => ({
        ...prev,
        [name]: issue?.message ?? 'Invalid value',
      }));
      return false;
    } catch (err) {
      console.error(err);
      setErrors(prev => ({ ...prev, [name]: 'Invalid value' }));
      return false;
    }
  };

  const clearFieldError = (name: string) => {
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const calculateDistance = async (
    pickupLocation: string,
    deliveryLocation: string,
    accessToken: string,
    coordinates?: QuoteAddressCoordinates
  ) => {
    const payload: Record<string, unknown> = {
      pickupLocation,
      deliveryLocation,
    };

    if (coordinates?.pickup) {
      payload.pickupCoordinates = coordinates.pickup;
    }
    if (coordinates?.delivery) {
      payload.deliveryCoordinates = coordinates.delivery;
    }

    const response = await fetch('/.netlify/functions/calculate-distance', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const msg =
        (data && typeof data.error === 'string' && data.error) ||
        (data && data.details) ||
        'Failed to calculate distance';
      throw new Error(msg);
    }
    return data;
  };

  const saveQuote = async (
    quoteData: any,
    isUpdating: boolean,
    recordId: string | null,
    userId: string
  ) => {
    let finalRecordId = recordId;
    let insertError;

    if (isUpdating && recordId) {
      const { error } = await supabase
        .from('quotes')
        .update({
          ...quoteData,
          updated_at: new Date().toISOString(),
        })
        .eq('id', recordId)
        .eq('user_id', userId);
      insertError = error;
    } else {
      const { data, error } = await supabase
        .from('quotes')
        .insert([quoteData])
        .select()
        .single();
      insertError = error;
      if (data) {
        finalRecordId = data.id;
      }
    }

    if (insertError) throw insertError;
    return finalRecordId;
  };

  const submitQuote = async (
    formData: QuoteFormData,
    isUpdating: boolean = false,
    recordId: string | null = null,
    coordinates?: QuoteAddressCoordinates
  ) => {
    setLoading(true);
    setErrors({});

    try {
      // 1. Verificar autenticación
      const session = await supabase.auth.getSession();
      if (!session.data.session) {
        throw new Error('Authentication required');
      }

      const accessToken = session.data.session.access_token;
      const userId = session.data.session.user.id;

      // 2. Validar datos
      const parsed = quoteSchema.safeParse(formData);

      if (!parsed.success) {
        const fieldErrors: { [key: string]: string } = {};
        Object.entries(parsed.error.formErrors.fieldErrors).forEach(
          ([field, messages]) => {
            if (messages?.length) {
              fieldErrors[field] = messages[0];
            }
          }
        );
        setErrors(fieldErrors);
        return { success: false, error: 'Please verify the entered data' };
      }

      const validatedData = parsed.data;

      // 3. Calcular distancia y precio
      const result = await calculateDistance(
        validatedData.pickupLocation,
        validatedData.deliveryLocation,
        accessToken,
        coordinates
      );

      const { distance, price, route_id } = result;

      // 4. Preparar datos para guardar
      const quoteData = {
        name: validatedData.name,
        email: validatedData.email,
        phone: validatedData.phone,
        pickup_location: validatedData.pickupLocation,
        delivery_location: validatedData.deliveryLocation,
        vehicle_type: validatedData.vehicleType,
        calculated_distance: Math.round(distance),
        final_price: price,
        user_id: userId,
        status: 'pending',
        mapbox_route_id: route_id,
      };

      // 5. Guardar cotización
      const finalRecordId = await saveQuote(quoteData, isUpdating, recordId, userId);

      // 6. Enviar email de confirmación
      try {
        await sendRequestConfirmation({
          email: formData.email,
          name: formData.name,
          type: 'quote',
          isUpdate: isUpdating,
          formData: {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            pickupLocation: formData.pickupLocation,
            deliveryLocation: formData.deliveryLocation,
            vehicleType: formData.vehicleType,
          },
        });
      } catch (emailError) {
        console.error('Failed to send confirmation email:', emailError);
      }

      // 7. Track successful form submission
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'form_submit', {
          event_category: 'engagement',
          event_label: 'quote_form',
          form_type: 'quote',
        });
      }

      // 8. Retornar éxito (el componente manejará el alert)
      return { success: true, recordId: finalRecordId };
    } catch (err: any) {
      const message = err?.message || 'Failed to submit quote request';
      setErrors({ submit: message });

      // El componente manejará los alerts, solo retornamos el error
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    errors,
    submitQuote,
    validateField,
    clearFieldError,
    setErrors,
  };
};